import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  DollarSign, 
  Loader2,
  FileText,
  Calendar,
  TrendingUp,
  TrendingDown,
  Download
} from 'lucide-react';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

interface ContratoComComissao {
  id: string;
  nome_cliente: string;
  valor_total: number;
  comissao_vendedor_valor: number;
  status: string;
  created_at: string;
  pagamentos: Pagamento[];
}

interface Pagamento {
  id: string;
  valor_pago: number;
  data_pagamento: string;
  forma_pagamento: string;
  observacoes?: string;
  comprovante_url?: string;
  comprovante_nome_arquivo?: string;
  origem?: 'comissao' | 'financeiro';
}

interface VendedorFinanceiroTabProps {
  vendedorId: string;
}

const FORMAS_PAGAMENTO = [
  { value: 'pix', label: 'PIX' },
  { value: 'transferencia', label: 'Transferência Bancária' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'outros', label: 'Outros' },
];

export function VendedorFinanceiroTab({ vendedorId }: VendedorFinanceiroTabProps) {
  const [contratos, setContratos] = useState<ContratoComComissao[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (vendedorId) {
      fetchContratos();
    }
  }, [vendedorId]);

  const fetchContratos = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`/api/vendedores/${vendedorId}/financeiro`);

      if (!response.ok) {
        throw new Error('Erro ao carregar dados financeiros');
      }

      const data = await response.json();
      setContratos(data.contratos || []);
    } catch (error) {
      console.error('Erro ao buscar contratos:', error);
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const calcularTotalComissao = () => {
    return contratos.reduce((total, contrato) => total + (contrato.comissao_vendedor_valor || 0), 0);
  };

  const calcularTotalPago = () => {
    return contratos.reduce((total, contrato) => {
      const pagamentosContrato = contrato.pagamentos?.reduce((sum, pag) => sum + pag.valor_pago, 0) || 0;
      return total + pagamentosContrato;
    }, 0);
  };

  const calcularSaldoDevedor = () => {
    return calcularTotalComissao() - calcularTotalPago();
  };

  const calcularSaldoContrato = (contrato: ContratoComComissao) => {
    const totalPago = contrato.pagamentos?.reduce((sum, pag) => sum + pag.valor_pago, 0) || 0;
    return contrato.comissao_vendedor_valor - totalPago;
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      'rascunho': { label: 'Rascunho', variant: 'secondary' },
      'proposta_aceita': { label: 'Proposta Aceita', variant: 'outline' },
      'contrato_gerado': { label: 'Contrato Gerado', variant: 'default' },
      'assinado': { label: 'Assinado', variant: 'default' },
    };
    const statusInfo = statusMap[status] || { label: status, variant: 'secondary' };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalComissao = calcularTotalComissao();
  const totalPago = calcularTotalPago();
  const saldoDevedor = calcularSaldoDevedor();

  return (
    <div className="space-y-6">
      {/* Resumo Financeiro */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total de Comissões
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(totalComissao)}
              </div>
              <DollarSign className="h-8 w-8 text-blue-600/20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Pago
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(totalPago)}
              </div>
              <TrendingUp className="h-8 w-8 text-green-600/20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Saldo Devedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className={`text-2xl font-bold ${saldoDevedor > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(saldoDevedor)}
              </div>
              <TrendingDown className={`h-8 w-8 ${saldoDevedor > 0 ? 'text-red-600/20' : 'text-green-600/20'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Contratos */}
      <Card>
        <CardHeader>
          <CardTitle>Contratos e Comissões</CardTitle>
        </CardHeader>
        <CardContent>
          {contratos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhum contrato encontrado</h3>
              <p className="text-muted-foreground text-center">
                Este vendedor ainda não possui contratos fechados.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Data do Contrato</TableHead>
                    <TableHead>Valor do Contrato</TableHead>
                    <TableHead>Comissão Devida</TableHead>
                    <TableHead>Total Pago</TableHead>
                    <TableHead>Saldo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contratos.map((contrato) => {
                    const totalPagoContrato = contrato.pagamentos?.reduce((sum, pag) => sum + pag.valor_pago, 0) || 0;
                    const saldoContrato = calcularSaldoContrato(contrato);
                    
                    return (
                      <TableRow key={contrato.id}>
                        <TableCell className="font-medium">{contrato.nome_cliente}</TableCell>
                        <TableCell>{formatDate(contrato.created_at)}</TableCell>
                        <TableCell>{formatCurrency(contrato.valor_total)}</TableCell>
                        <TableCell className="font-semibold text-blue-600">
                          {formatCurrency(contrato.comissao_vendedor_valor)}
                        </TableCell>
                        <TableCell className="font-semibold text-green-600">
                          {formatCurrency(totalPagoContrato)}
                        </TableCell>
                        <TableCell className={`font-semibold ${saldoContrato > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(saldoContrato)}
                        </TableCell>
                        <TableCell>{getStatusBadge(contrato.status)}</TableCell>
                        <TableCell className="text-right">
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico de Pagamentos */}
      {contratos.some(c => c.pagamentos && c.pagamentos.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Histórico de Pagamentos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {contratos.map((contrato) => {
                if (!contrato.pagamentos || contrato.pagamentos.length === 0) return null;
                
                return (
                  <div key={contrato.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-semibold">{contrato.nome_cliente}</h4>
                        <p className="text-sm text-muted-foreground">
                          Contrato de {formatCurrency(contrato.valor_total)}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {contrato.pagamentos.map((pagamento) => (
                        <div key={pagamento.id} className="flex items-center justify-between p-2 bg-muted rounded">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{formatDate(pagamento.data_pagamento)}</span>
                            </div>
                            <Badge variant="outline">
                              {FORMAS_PAGAMENTO.find(f => f.value === pagamento.forma_pagamento)?.label || pagamento.forma_pagamento}
                            </Badge>
                            {pagamento.origem && (
                              <Badge variant={pagamento.origem === 'comissao' ? 'secondary' : 'default'} className="text-[10px] h-5">
                                {pagamento.origem === 'comissao' ? 'Comissão' : 'Financeiro'}
                              </Badge>
                            )}
                            {pagamento.observacoes && (
                              <span className="text-sm text-muted-foreground italic">
                                {pagamento.observacoes}
                              </span>
                            )}
                            {pagamento.comprovante_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(pagamento.comprovante_url, '_blank')}
                                className="h-7"
                              >
                                <Download className="h-3 w-3 mr-1" />
                                {pagamento.comprovante_nome_arquivo || 'Ver Comprovante'}
                              </Button>
                            )}
                          </div>
                          <span className="font-semibold text-green-600">
                            {formatCurrency(pagamento.valor_pago)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

